import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  StorageDirectory,
  StorageItem,
  StorageUsage,
  DirectoryWithChildren,
  ViewMode,
  SortBy,
  SortOrder,
  Breadcrumb,
  StorageLimitError,
} from '../../types/storage';
import { buildBreadcrumbs } from '../../utils/storageUtils';

interface StorageState {
  currentDirectoryId: number | null;
  breadcrumbs: Breadcrumb[];
  allDirectories: StorageDirectory[];
  currentDirectoryData: DirectoryWithChildren | null;
  items: StorageItem[];
  storageUsage: StorageUsage | null;
  selectedItemIds: number[];
  selectedDirectoryIds: number[];
  viewMode: ViewMode;
  sortBy: SortBy;
  sortOrder: SortOrder;
  searchQuery: string;

  isLoading: boolean;
  isUploading: boolean;
  uploadProgress: number;

  showCreateFolderModal: boolean;
  showMoveModal: boolean;
  showDeleteConfirmModal: boolean;
  showStorageLimitModal: boolean;
  storageLimitError: StorageLimitError | null;

  error: string | null;
}

const initialState: StorageState = {
  currentDirectoryId: null,
  breadcrumbs: [{ id: null, name: 'My Files' }],
  allDirectories: [],
  currentDirectoryData: null,
  items: [],
  storageUsage: null,
  selectedItemIds: [],
  selectedDirectoryIds: [],
  viewMode: 'list',
  sortBy: 'name',
  sortOrder: 'asc',
  searchQuery: '',
  isLoading: false,
  isUploading: false,
  uploadProgress: 0,
  showCreateFolderModal: false,
  showMoveModal: false,
  showDeleteConfirmModal: false,
  showStorageLimitModal: false,
  storageLimitError: null,
  error: null,
};

const storageSlice = createSlice({
  name: 'storage',
  initialState,
  reducers: {
    // Loading states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },

    setUploading: (state, action: PayloadAction<boolean>) => {
      state.isUploading = action.payload;
      if (!action.payload) {
        state.uploadProgress = 0;
      }
    },

    setUploadProgress: (state, action: PayloadAction<number>) => {
      state.uploadProgress = action.payload;
    },

    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isLoading = false;
      state.isUploading = false;
    },

    clearError: (state) => {
      state.error = null;
    },

    // Data actions
    setStorageUsage: (state, action: PayloadAction<StorageUsage>) => {
      state.storageUsage = action.payload;
    },

    setDirectoryTree: (state, action: PayloadAction<DirectoryWithChildren[]>) => {
      const flattenTree = (nodes: DirectoryWithChildren[]): StorageDirectory[] => {
        let result: StorageDirectory[] = [];
        nodes.forEach((node) => {
          result.push({
            id: node.id,
            businessId: node.businessId,
            name: node.name,
            parentId: node.parentId,
            createdAt: node.createdAt,
            updatedAt: node.updatedAt,
          });
          if (node.children.length > 0) {
            result = result.concat(flattenTree(node.children));
          }
        });
        return result;
      };
      state.allDirectories = flattenTree(action.payload);
      state.breadcrumbs = buildBreadcrumbs(state.currentDirectoryId, state.allDirectories);
      state.isLoading = false;
    },

    setCurrentDirectoryData: (state, action: PayloadAction<DirectoryWithChildren>) => {
      state.currentDirectoryData = action.payload;
      state.items = action.payload.items;
    },

    setItems: (state, action: PayloadAction<StorageItem[]>) => {
      state.items = action.payload;
      state.isLoading = false;
    },

    addDirectory: (state, action: PayloadAction<StorageDirectory>) => {
      state.allDirectories.push(action.payload);
      state.showCreateFolderModal = false;
    },

    updateDirectoryData: (state, action: PayloadAction<StorageDirectory>) => {
      const index = state.allDirectories.findIndex((d) => d.id === action.payload.id);
      if (index !== -1) {
        state.allDirectories[index] = action.payload;
      }
      state.breadcrumbs = buildBreadcrumbs(state.currentDirectoryId, state.allDirectories);
    },

    removeDirectory: (state, action: PayloadAction<number>) => {
      state.allDirectories = state.allDirectories.filter((d) => d.id !== action.payload);
      state.selectedDirectoryIds = state.selectedDirectoryIds.filter((id) => id !== action.payload);
      state.showDeleteConfirmModal = false;
    },

    addItems: (state, action: PayloadAction<StorageItem[]>) => {
      state.items.push(...action.payload);
      state.isUploading = false;
      state.uploadProgress = 100;
    },

    updateItemData: (state, action: PayloadAction<StorageItem>) => {
      const index = state.items.findIndex((i) => i.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    },

    removeItem: (state, action: PayloadAction<number>) => {
      state.items = state.items.filter((i) => i.id !== action.payload);
      state.selectedItemIds = state.selectedItemIds.filter((id) => id !== action.payload);
      state.showDeleteConfirmModal = false;
    },

    removeItems: (state, action: PayloadAction<number[]>) => {
      state.items = state.items.filter((i) => !action.payload.includes(i.id));
      state.selectedItemIds = [];
      state.showDeleteConfirmModal = false;
    },

    moveItemsFromCurrent: (state, action: PayloadAction<{ itemIds: number[]; newDirectoryId: number | null }>) => {
      if (action.payload.newDirectoryId !== state.currentDirectoryId) {
        state.items = state.items.filter((item) => !action.payload.itemIds.includes(item.id));
      }
      state.selectedItemIds = [];
      state.showMoveModal = false;
    },

    // Navigation
    setCurrentDirectory: (state, action: PayloadAction<number | null>) => {
      state.currentDirectoryId = action.payload;
      state.breadcrumbs = buildBreadcrumbs(action.payload, state.allDirectories);
      state.selectedItemIds = [];
      state.selectedDirectoryIds = [];
    },

    // Selection
    selectItem: (state, action: PayloadAction<number>) => {
      if (!state.selectedItemIds.includes(action.payload)) {
        state.selectedItemIds.push(action.payload);
      }
    },

    deselectItem: (state, action: PayloadAction<number>) => {
      state.selectedItemIds = state.selectedItemIds.filter((id) => id !== action.payload);
    },

    toggleItemSelection: (state, action: PayloadAction<number>) => {
      const index = state.selectedItemIds.indexOf(action.payload);
      if (index === -1) {
        state.selectedItemIds.push(action.payload);
      } else {
        state.selectedItemIds.splice(index, 1);
      }
    },

    selectDirectory: (state, action: PayloadAction<number>) => {
      if (!state.selectedDirectoryIds.includes(action.payload)) {
        state.selectedDirectoryIds.push(action.payload);
      }
    },

    deselectDirectory: (state, action: PayloadAction<number>) => {
      state.selectedDirectoryIds = state.selectedDirectoryIds.filter((id) => id !== action.payload);
    },

    toggleDirectorySelection: (state, action: PayloadAction<number>) => {
      const index = state.selectedDirectoryIds.indexOf(action.payload);
      if (index === -1) {
        state.selectedDirectoryIds.push(action.payload);
      } else {
        state.selectedDirectoryIds.splice(index, 1);
      }
    },

    selectAllItems: (state) => {
      state.selectedItemIds = state.items.map((item) => item.id);
    },

    clearSelection: (state) => {
      state.selectedItemIds = [];
      state.selectedDirectoryIds = [];
    },

    // View settings
    setViewMode: (state, action: PayloadAction<ViewMode>) => {
      state.viewMode = action.payload;
    },

    setSortBy: (state, action: PayloadAction<SortBy>) => {
      state.sortBy = action.payload;
    },

    setSortOrder: (state, action: PayloadAction<SortOrder>) => {
      state.sortOrder = action.payload;
    },

    toggleSortOrder: (state) => {
      state.sortOrder = state.sortOrder === 'asc' ? 'desc' : 'asc';
    },

    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },

    // Modals
    setShowCreateFolderModal: (state, action: PayloadAction<boolean>) => {
      state.showCreateFolderModal = action.payload;
    },

    setShowMoveModal: (state, action: PayloadAction<boolean>) => {
      state.showMoveModal = action.payload;
    },

    setShowDeleteConfirmModal: (state, action: PayloadAction<boolean>) => {
      state.showDeleteConfirmModal = action.payload;
    },

    setShowStorageLimitModal: (state, action: PayloadAction<boolean>) => {
      state.showStorageLimitModal = action.payload;
    },

    setStorageLimitError: (state, action: PayloadAction<StorageLimitError | null>) => {
      state.storageLimitError = action.payload;
      if (action.payload) {
        state.showStorageLimitModal = true;
      }
    },
  },
});

export const {
  setLoading,
  setUploading,
  setUploadProgress,
  setError,
  clearError,
  setStorageUsage,
  setDirectoryTree,
  setCurrentDirectoryData,
  setItems,
  addDirectory,
  updateDirectoryData,
  removeDirectory,
  addItems,
  updateItemData,
  removeItem,
  removeItems,
  moveItemsFromCurrent,
  setCurrentDirectory,
  selectItem,
  deselectItem,
  toggleItemSelection,
  selectDirectory,
  deselectDirectory,
  toggleDirectorySelection,
  selectAllItems,
  clearSelection,
  setViewMode,
  setSortBy,
  setSortOrder,
  toggleSortOrder,
  setSearchQuery,
  setShowCreateFolderModal,
  setShowMoveModal,
  setShowDeleteConfirmModal,
  setShowStorageLimitModal,
  setStorageLimitError,
} = storageSlice.actions;

export default storageSlice.reducer;
